import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { DataScope, PageResponse } from './types'

/**
 * Administration: users, roles, permissions, and the create/edit/delete half of every
 * master. The read hooks for masters live in hooks.ts and execution.ts; this file adds
 * the writes, so a screen that only lists things does not pull mutations into its bundle.
 */

// --------------------------------------------------------------------- users

export interface UserRoleRef {
  id: string
  code: string
  name: string
}

export interface AdminUser {
  id: string
  companyId: string
  username: string
  fullName: string
  email?: string
  mobile?: string
  employeeCode?: string
  designation?: string
  dataScope: DataScope
  active: boolean
  locked: boolean
  mustChangePassword: boolean
  lastLoginAt?: string
  roles: UserRoleRef[]
  projectIds: string[]
  siteIds: string[]
  permissionCount: number
  version: number
}

export interface UserCreateRequest {
  username: string
  fullName: string
  email?: string
  mobile?: string
  employeeCode?: string
  designation?: string
  password?: string
  dataScope: DataScope
  roleIds: string[]
  projectIds?: string[]
  siteIds?: string[]
}

export interface UserUpdateRequest {
  fullName: string
  email?: string
  mobile?: string
  employeeCode?: string
  designation?: string
  dataScope?: DataScope
  active?: boolean
}

/** Returned once when the password was generated rather than typed in. */
export interface CredentialResponse {
  id: string
  username: string
  generatedPassword?: string
}

export function useUsers(params: { page: number; size: number }) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const { data } = await api.get<PageResponse<AdminUser>>('/users', { params })
      return data
    },
  })
}

function useUserMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useCreateUser() {
  return useUserMutation(async (body: UserCreateRequest) => {
    const { data } = await api.post<CredentialResponse>('/users', body)
    return data
  })
}

export function useUpdateUser() {
  return useUserMutation(async ({ id, body }: { id: string; body: UserUpdateRequest }) => {
    const { data } = await api.put<AdminUser>(`/users/${id}`, body)
    return data
  })
}

export function useSetUserRoles() {
  return useUserMutation(async ({ id, roleIds }: { id: string; roleIds: string[] }) => {
    const { data } = await api.put<AdminUser>(`/users/${id}/roles`, { roleIds })
    return data
  })
}

export function useSetUserScope() {
  return useUserMutation(
    async ({ id, projectIds, siteIds }: { id: string; projectIds: string[]; siteIds: string[] }) => {
      const { data } = await api.put<AdminUser>(`/users/${id}/scope`, { projectIds, siteIds })
      return data
    },
  )
}

export function useResetUserPassword() {
  return useUserMutation(async ({ id, newPassword }: { id: string; newPassword?: string }) => {
    const { data } = await api.post<CredentialResponse>(`/users/${id}/reset-password`, {
      newPassword: newPassword || null,
    })
    return data
  })
}

export function useUnlockUser() {
  return useUserMutation(async (id: string) => {
    const { data } = await api.post<AdminUser>(`/users/${id}/unlock`)
    return data
  })
}

export function useDeleteUser() {
  return useUserMutation(async (id: string) => {
    await api.delete(`/users/${id}`)
  })
}

// --------------------------------------------------------------------- roles

export interface AdminRole {
  id: string
  companyId?: string
  code: string
  name: string
  description?: string
  defaultScope: DataScope
  system: boolean
  active: boolean
  permissionCodes: string[]
  userCount: number
  version: number
}

export interface RoleSaveRequest {
  code: string
  name: string
  description?: string
  defaultScope: DataScope
  active?: boolean
  permissionCodes?: string[]
}

export interface PermissionEntry {
  code: string
  module: string
  action: string
  description?: string
}

export interface PermissionModule {
  module: string
  permissions: PermissionEntry[]
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await api.get<AdminRole[]>('/roles')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function usePermissionCatalogue() {
  return useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: async () => {
      const { data } = await api.get<PermissionModule[]>('/roles/permissions')
      return data
    },
    staleTime: 60 * 60 * 1000,
  })
}

function useRoleMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] })
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useCreateRole() {
  return useRoleMutation(async (body: RoleSaveRequest) => {
    const { data } = await api.post<AdminRole>('/roles', body)
    return data
  })
}

export function useUpdateRole() {
  return useRoleMutation(async ({ id, body }: { id: string; body: RoleSaveRequest }) => {
    const { data } = await api.put<AdminRole>(`/roles/${id}`, body)
    return data
  })
}

export function useSetRolePermissions() {
  return useRoleMutation(
    async ({ id, permissionCodes }: { id: string; permissionCodes: string[] }) => {
      const { data } = await api.put<AdminRole>(`/roles/${id}/permissions`, { permissionCodes })
      return data
    },
  )
}

export function useDeleteRole() {
  return useRoleMutation(async (id: string) => {
    await api.delete(`/roles/${id}`)
  })
}

// -------------------------------------------------------------- master edits

/**
 * Create, update and delete for one master. The list hooks stay where they are, so this
 * only supplies the writes and invalidates whatever key the list is cached under.
 */
export function masterWrites<TBody, TResult = unknown>(path: string, invalidate: unknown[]) {
  function useInvalidator() {
    const qc = useQueryClient()
    return () => {
      for (const key of invalidate) {
        void qc.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
      }
    }
  }

  return {
    useCreate() {
      const invalidateAll = useInvalidator()
      return useMutation({
        mutationFn: async (body: TBody) => {
          const { data } = await api.post<TResult>(path, body)
          return data
        },
        onSuccess: invalidateAll,
      })
    },
    useUpdate() {
      const invalidateAll = useInvalidator()
      return useMutation({
        mutationFn: async ({ id, body }: { id: string; body: TBody }) => {
          const { data } = await api.put<TResult>(`${path}/${id}`, body)
          return data
        },
        onSuccess: invalidateAll,
      })
    },
    useDelete() {
      const invalidateAll = useInvalidator()
      return useMutation({
        mutationFn: async (id: string) => {
          await api.delete(`${path}/${id}`)
        },
        onSuccess: invalidateAll,
      })
    },
  }
}

export const uomApi = masterWrites('/masters/uoms', [['masters', 'uoms']])
export const materialCategoryApi = masterWrites('/masters/material-categories', [
  ['masters', 'material-categories'],
])
export const labourCategoryApi = masterWrites('/masters/labour-categories', [
  ['masters', 'labour-categories'],
])
export const workPackageApi = masterWrites('/masters/work-packages', [['masters', 'work-packages']])
export const workCategoryApi = masterWrites('/masters/work-categories', [['work-categories']])
export const materialApi = masterWrites('/masters/materials', [['materials']])
export const disciplineApi = masterWrites('/masters/disciplines', [
  ['disciplines'],
  ['disciplines', 'all'],
])
export const drawingTypeApi = masterWrites('/masters/drawing-types', [
  ['drawing-types'],
  ['drawing-types', 'all'],
])
export const issueCategoryApi = masterWrites('/masters/issue-categories', [
  ['issue-categories'],
  ['issue-categories', 'all'],
])

export interface OptionDetail {
  id: string
  code: string
  name: string
  sortOrder: number
  defaultPriority?: string
  active: boolean
  version: number
}

/** The admin grids list retired rows too, which the pickers deliberately hide. */
function optionList(key: string, path: string) {
  return function useOptionList() {
    return useQuery({
      queryKey: [key, 'all'],
      queryFn: async () => {
        const { data } = await api.get<OptionDetail[]>(path)
        return data
      },
    })
  }
}

export const useAllDisciplines = optionList('disciplines', '/masters/disciplines/all')
export const useAllDrawingTypes = optionList('drawing-types', '/masters/drawing-types/all')
export const useAllIssueCategories = optionList('issue-categories', '/masters/issue-categories/all')

export function useWorkCategories(workPackageId?: string) {
  return useQuery({
    enabled: Boolean(workPackageId),
    queryKey: ['work-categories', workPackageId],
    queryFn: async () => {
      const { data } = await api.get<
        { id: string; workPackageId: string; code: string; name: string; sortOrder: number; active: boolean }[]
      >(`/masters/work-packages/${workPackageId}/categories`)
      return data
    },
  })
}

export function useCreateWorkCategory(workPackageId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.post(`/masters/work-packages/${workPackageId}/categories`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-categories'] })
    },
  })
}

// ------------------------------------------------------------------ parties

export type PartyType = 'clients' | 'consultants' | 'contractors' | 'suppliers'

export interface Party {
  id: string
  type: string
  code: string
  name: string
  contactPerson?: string
  email?: string
  mobile?: string
  address?: string
  gstNo?: string
  discipline?: string
  canIssueDrawings?: boolean
  trade?: string
  suppliesLabour?: boolean
  rating?: number
  active: boolean
  version: number
}

export interface PartySaveRequest {
  code: string
  name: string
  contactPerson?: string
  email?: string
  mobile?: string
  address?: string
  gstNo?: string
  discipline?: string
  canIssueDrawings?: boolean
  trade?: string
  suppliesLabour?: boolean
  rating?: number | null
  active?: boolean
}

export function useParties(type: PartyType) {
  return useQuery({
    queryKey: ['parties', type],
    queryFn: async () => {
      const { data } = await api.get<Party[]>(`/parties/${type}`)
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

function usePartyMutation<TVars, TResult>(type: PartyType, fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['parties', type] })
      // The daily forms cache these under their own singular keys.
      void qc.invalidateQueries({ queryKey: [type] })
    },
  })
}

export function useCreateParty(type: PartyType) {
  return usePartyMutation(type, async (body: PartySaveRequest) => {
    const { data } = await api.post<Party>(`/parties/${type}`, body)
    return data
  })
}

export function useUpdateParty(type: PartyType) {
  return usePartyMutation(type, async ({ id, body }: { id: string; body: PartySaveRequest }) => {
    const { data } = await api.put<Party>(`/parties/${type}/${id}`, body)
    return data
  })
}

export function useDeleteParty(type: PartyType) {
  return usePartyMutation(type, async (id: string) => {
    await api.delete(`/parties/${type}/${id}`)
  })
}
